import { IWizardSpecification } from '../models/i-wizard-specification';
import { Injectable } from '@angular/core';
import { BaseFormConfigContainer } from '../../../../order-builder/modules/specification/models/base-form-config-container';
import { FieldConfig } from '../models/field-configs/field-config.interface';
import { DynamicEffectsRegistratorService } from '../../dynamic-effects/dynamic-effects-registrator.service';
import { _safe } from '../../../utils/safe-get.method';
import { ORDER_EXPERIENCES } from '@ordersnproducts/modules/shared/constants/order-experiences';

@Injectable()
export class FormConfigLoader {
  private registeredEffects = [];

  constructor(private effectsRegistrator: DynamicEffectsRegistratorService) {}

  /**
   * Loads a form-field configuration based on an IWizardSpecification which states country, business-line, and delivery-method.
   * @param {BaseFormConfigContainer} formConfig
   * @param {IWizardSpecification} specs
   * @param {boolean} registerEffects
   * @param {boolean} strict If set to true will fail if the config is not found
   * @param {string} objectName
   * @return {BaseFormConfig}
   */
  public loadConfigBySpecification<T>(
    formConfig: BaseFormConfigContainer<T>,
    specs: IWizardSpecification,
    registerEffects: boolean = true,
    strict: boolean = true,
    objectName: string = 'fields',
  ): T {
    const getObject: (object: object, propertyName: string) => any = strict ? this.getObjectOrFail : this.getObjectSafe;
    const countryConf = getObject(formConfig, specs.country);
    const businessLineConf = getObject(countryConf, specs.businessLine);
    let finalConf: T;
    if (specs.experienceCode && specs.experienceCode !== ORDER_EXPERIENCES.REQUEST) {
      const deliveryConf = getObject(businessLineConf, specs.delivery);
      finalConf = getObject(deliveryConf, specs.experienceCode) as T;
    } else {
      const requestConf = _safe(() => businessLineConf[specs.delivery][ORDER_EXPERIENCES.REQUEST]);
      finalConf = requestConf ? requestConf : (getObject(businessLineConf, specs.delivery) as T);
    }

    if (registerEffects) {
      this.registerEffectsFromConfig<T>(finalConf, objectName);
    }
    return finalConf;
  }

  /**
   * Takes a list of Effects from a configuration file, and applies them to the current form.
   * @param {T} finalConf
   * @param {string} path
   */
  public registerEffectsFromConfig<T>(finalConf: T, path: string) {
    const effects = this.getEffectsFromConfigs(finalConf[path]);
    this.registerDynamicEffects(effects);
  }

  /**
   * Takes a list of Effects related to each path, and applies them to the current form.
   *
   * @param finalConf
   * @param paths
   */
  public registerEffectsFromMultipleConfigs<T>(finalConf: T, paths: string[]) {
    let effects = paths
      .map((path) => this.getEffectsFromConfigs(finalConf[path] || []))
      .reduce((acc, val) => acc.concat(val), []);

    effects = [...new Set(effects)];
    this.registerDynamicEffects(effects);
  }

  /**
   * Gets propertyName from object, or throws an error if it fails to find it.
   * @param {object} object
   * @param {string} propertyName
   * @return {any}
   */
  protected getObjectOrFail(object: object, propertyName: string): any {
    if (!object.hasOwnProperty(propertyName)) {
      throw new Error(
        propertyName +
          ' not defined in config object.' +
          ' You probably forgot to define this configuration, or the data is wrong.',
      );
    }
    return object[propertyName];
  }

  /**
   * Gets propertyName from object, returns undefined if it fails to find it
   * @param {object} object
   * @param {string} propertyName
   * @return {any}
   */
  protected getObjectSafe(object: object, propertyName: string): any {
    return _safe(() => object[propertyName]);
  }

  /**
   * Replaces the current set of registered effects with a new set.  Essentially, this attaches a fresh set of
   * plug-in behaviours to a form.
   * @param effects
   */
  public registerDynamicEffects(effects) {
    this.effectsRegistrator.removeEffectByTypes(this.registeredEffects);
    this.effectsRegistrator.registerEffectsByType(effects);
    this.registeredEffects = effects;
  }

  /**
   * When a configuration uses dynamically-loaded Effects to carry out tasks as side-effects, this function retrieves
   * them, accumulates them, and returns them as an array.
   * @param {FieldConfig[]} configs
   * @return {any[]}
   */
  public getEffectsFromConfigs(configs: FieldConfig[]) {
    return configs.reduce((acc, config) => {
      if (!(config.dynamicEffects && Array.isArray(config.dynamicEffects))) {
        return acc;
      }

      return [...acc, ...config.dynamicEffects];
    }, []);
  }
}
